import React, { useMemo } from "react";
import {
  LuChevronLeft,
  LuChevronsLeft,
  LuChevronsRight,
  LuChevronRight,
} from "react-icons/lu";

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  containerClassName?: string;
}

const MAX_VISIBLE = 5; // current + 2 prev + 2 next
const DOTS = "…";

function range(start: number, end: number): number[] {
  const len = Math.max(0, end - start + 1);
  return Array.from({ length: len }, (_, i) => start + i);
}

function getWindow(current: number, total: number): number[] {
  if (total <= MAX_VISIBLE) return range(1, total);

  let start = Math.max(1, current - 2);
  let end = Math.min(total, current + 2);

  // Adjust to always show 5 items
  if (end - start + 1 < MAX_VISIBLE) {
    if (start === 1) {
      end = Math.min(total, start + MAX_VISIBLE - 1);
    } else if (end === total) {
      start = Math.max(1, total - MAX_VISIBLE + 1);
    }
  }

  return range(start, end);
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  containerClassName,
}) => {
  const windowPages = useMemo(
    () => getWindow(Math.min(Math.max(currentPage, 1), totalPages), totalPages),
    [currentPage, totalPages]
  );

  const goTo = (p: number) => {
    const safe = Math.min(Math.max(p, 1), totalPages);
    if (safe !== currentPage) onPageChange(safe);
  };

  const isFirst = currentPage <= 1;
  const isLast = currentPage >= totalPages;

  return (
    <nav
      aria-label="Pagination"
      className={`my-4 2xl:my-8 flex justify-center items-center gap-2 ${
        containerClassName ?? ""
      }`}
    >
      {/* Jump to first */}
      <button
        type="button"
        className="px-3 py-2 text-xs 2xl:text-sm text-gray-600 rounded-md hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={() => goTo(1)}
        disabled={isFirst}
      >
        <LuChevronsLeft />
      </button>

      {/* Previous */}
      <button
        type="button"
        className="px-3 py-2 text-xs 2xl:text-sm text-gray-600 rounded-md hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={() => goTo(currentPage - 1)}
        disabled={isFirst}
      >
        <LuChevronLeft />
      </button>

      {/* Leading dots */}
      {windowPages[0] > 1 && (
        <>
          <span className="px-2 text-gray-400 select-none">{DOTS}</span>
        </>
      )}

      {/* Numbered buttons */}
      {windowPages.map((p) => (
        <button
          type="button"
          key={p}
          onClick={() => goTo(p)}
          aria-current={currentPage === p ? "page" : undefined}
          className={` text-center text-xs 2xl:text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/50 w-7 h-7 ${
            currentPage === p
              ? "bg-blue-600 text-white"
              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}
        >
          {p}
        </button>
      ))}

      {/* Trailing dots */}
      {windowPages[windowPages.length - 1] < totalPages && (
        <>
          <span className="px-2 text-gray-400 select-none">{DOTS}</span>
        </>
      )}

      {/* Next */}
      <button
        type="button"
        className="px-3 py-2 text-xs 2xl:text-sm text-gray-600 rounded-md hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={() => goTo(currentPage + 1)}
        disabled={isLast}
        aria-label="Next page"
      >
        <LuChevronRight />
      </button>

      {/* Jump to last */}
      <button
        type="button"
        className="px-3 py-2 text-xs 2xl:text-sm text-gray-600 rounded-md hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={() => goTo(totalPages)}
        disabled={isLast}
        aria-label="Last page"
      >
        <LuChevronsRight />
      </button>
    </nav>
  );
};

export default Pagination;
